<template>
  <div class="indices-list">
    <page-header header="Tellimer Indices">
      <router-link
        v-if="$login.can('indices.create')"
        :to="{ name: 'indices.create' }"
        class="btn btn-primary"
      >
        Create new index
      </router-link>
    </page-header>

    <div class="mt-3">
      <search @input="updateSearchParam" class="mb-3" />

      <vue-good-table
        :rows="indices"
        :columns="columns"
        styleClass="vgt-table"
        @on-row-click="({ row }) => onRowClick(row)"
        :sort-options="{
          enabled: false,
        }"
      >
        <template v-slot:table-row="props">
          <template v-if="props.column.field === 'analysts'">
            <div v-if="value(props.row.analysts).length"
              class="d-flex" style="white-space: nowrap;text-overflow: ellipsis;overflow: hidden;">
              <div
                v-for="analyst in value(props.row).analysts"
                :key="analyst.id"
                class="article__analyst"
                style="width: 24px;height: 24px;"
              >
                <img
                  v-tooltip.top="analyst.name"
                  :src="analyst.picture.round"
                  class="rounded-circle img-circle-bg border-white article__analyst__img"
                  width="24"
                >
              </div>
            </div>
          </template>
          <template v-else-if="props.column.field === 'channel'">
            <img
              v-if="value(props.row).channel"
              v-tooltip.top="value(props.row).channel.name"
              :src="value(props.row).channel.badge"
              height="24"
              class="mr-1"
            >
          </template>
          <template v-else-if="props.column.field === 'lastUpdated'">
            <div
              style="white-space: nowrap;text-overflow: ellipsis;overflow: hidden;"
              v-if="value(props.row).updatedAt"
            >
              {{value(props.row).updatedAt}}
            </div>
          </template>
          <template v-else-if="props.column.field === 'menu'">
            <dropdown
              alignment="right"
              trigger="click"
              class="no-chev"
              @click.native.stop
            >
              <button
                slot="trigger"
                v-if="showEdit(props) || showDelete(props)"
                class="btn btn-link mr-3 no-chev"
              >
                <icon icon="more-vertical" />
              </button>

              <dropdown-option v-if="showEdit(props)"
                               :href="$router.resolve({ name: 'indices.edit', params: { slug: value(props.row).slug } }).href"
                               style="text-decoration: none;margin: 2px;"
              >
                <icon icon="edit" /> Edit
              </dropdown-option>
              <dropdown-option v-if="showDelete(props)"
                               style="text-decoration: none;"
                               href="javascript:;"
                               @click.native="() => showDeleteModal(props.row)"
              >
                <icon icon="delete" /> Delete
              </dropdown-option>
            </dropdown>
          </template>

          <template v-else-if="props.column.field === 'title'">
            <div>
              {{value(props.row).title}} <span v-if="value(props.row).isDraft" class="badge bg-primary text-light">Draft</span>
            </div>
          </template>
          <template v-else>{{value(props.row[props.column.field])}}</template>
        </template>
      </vue-good-table>

      <pagination
        :data="pagination"
        :disabled="loading"
        @change-page="changePage"
      />

      <confirm
        v-if="selectedIndex"
        :name="confirmHeader(selectedIndex)"
        @confirmed="removeIndex"
      />
    </div>
  </div>
</template>

<script>
import {Vue, Component, Prop, namespace} from 'nuxt-property-decorator'
import { VueGoodTable } from 'vue-good-table'
import Dropdown from '@/components/Dropdown/Dropdown.vue'
import DropdownOption from '@/components/Dropdown/DropdownOption.vue'
import Confirm from '@/components/Confirm.vue'
import Search from '@/components/Search.vue'
import Pagination from '@/components/Pagination.vue'
import {createHttpQuery} from '@/helpers'
import gateway from '@/gateway'

const defaultParams = {
  page: 1,
  search: null,
}

@Component({
  components: {
    Confirm,
    Dropdown,
    DropdownOption,
    VueGoodTable,
    Search,
    Pagination,
  },
  metaInfo() {
    return {
      title: 'Indices',
    }
  },
})
export default class IndicesListView extends Vue {

  @namespace('indices').Getter('pageIndices') indices
  @namespace('indices').Getter pagination
  @namespace('indices').Action getPaginatedIndices

  params = defaultParams
  selectedIndex = null

  @Prop({default: false, type: Boolean}) loading

  @Prop({default: () => ([
      {
        label: 'Title',
        field: 'title',
      },
      {
        label: 'Analysts',
        field: 'analysts',
      },
      {
        label: 'Channel',
        field: 'channel',
      },
      {
        label: 'Last Updated',
        field: 'lastUpdated',
      },
      {
        label: '',
        field: 'menu'
      },
    ])}) columns

  async getData() {
    this.getPaginatedIndices(createHttpQuery(this.params))
  }

  created () {
    this.params = {
      ...this.params,
      ...this.$route.query,
    }
    this.getData()
  }

  changePage(page = 1) {
    if (this.params.page === page) return
    this.params.page = page
    const { params } = this
    history.pushState(
      {},
      null,
      this.$router.resolve({ query: { ...params, page: page.toString() } }).href
    )
    this.getData()
  }

  updateSearchParam(search = '') {
    if (this.params.search === search) return

    this.params.search = search
    this.params.page = 1
    const {params} = this
    history.pushState(
      {},
      null,
      this.$router.resolve({query: {...params}}).href
    )
    this.getData()
  }

  onRowClick(index) {
    if (!this.showEdit()) return
    this.$router.push({
      name: 'indices.edit',
      params: {
        slug: index.slug,
      },
    })
  }

  showEdit () {
    return this.$login.can('indices.update')
  }

  showDelete () {
    return this.$login.can('indices.delete')
  }

  async removeIndex () {
    const {slug} = this.selectedIndex
    try {
      await gateway.delete(`cms/engage/indices/${slug}/delete`, )
      await this.getData()
    } catch (_) {
      //
    }
  }

  value (v) {
    return v || {}
  }

  confirmHeader (index) {
    return index.title || 'Untitled'
  }

  showDeleteModal (index) {
    this.selectedIndex = index
    this.$nextTick(() => this.$modal.show('confirm-delete'))
  }
}
</script>
